import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  AdminApproveLegacySettlesMutation,
  AdminApproveLegacySettlesMutationVariables,
  AdminLegacyPendingSettlementPreviewQuery,
  AdminLegacyPendingSettlementPreviewQueryVariables,
  AdminLegacySettlementCalendarQuery,
  AdminLegacySettlementCalendarQueryVariables,
  AdminLegacyRequestedSettlesQuery,
  AdminLegacyUnsettledStoresQuery,
  AdminRequestLegacySettleMutation,
  AdminRequestLegacySettleMutationVariables,
} from "../../../__generated__/graphql";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import LegacySettlementCalendar from "./components/legacy-settlement-calendar";

const ADMIN_LEGACY_UNSETTLED_STORES = gql(`
  query AdminLegacyUnsettledStores {
    adminLegacyUnsettledStores {
      sellerId
      storeId
      storeTitle
      storeAddress
      sellerName
      contactNumber
      businessNumber
      bank
      accountHolder
      accountNumber
      taxationType
      canIssueInvoice
      totalPrice
      settlePrice
      orderCount
      unrequestedOrderCount
      rejectedOrderCount
      isSettleInfoComplete
      cutoffDate
    }
  }
`);

const ADMIN_LEGACY_PENDING_SETTLEMENT_PREVIEW = gql(`
  query AdminLegacyPendingSettlementPreview($sellerId: Int!) {
    adminLegacyPendingSettlementPreview(sellerId: $sellerId) {
      sellerId
      totalPrice
      settlePrice
      orderCount
      unrequestedOrderCount
      rejectedOrderCount
      isSettleInfoComplete
      cutoffDate
    }
  }
`);

const ADMIN_LEGACY_REQUESTED_SETTLES = gql(`
  query AdminLegacyRequestedSettles {
    adminLegacyRequestedSettles {
      settleId
      sellerId
      storeId
      storeTitle
      requestDate
      settleWay
      settlePrice
      state
      orderCount
    }
  }
`);

const ADMIN_REQUEST_LEGACY_SETTLE = gql(`
  mutation AdminRequestLegacySettle($sellerId: Int!, $settleWay: String!) {
    adminRequestLegacySettle(sellerId: $sellerId, settleWay: $settleWay) {
      ok
      error
      settleId
    }
  }
`);

const ADMIN_APPROVE_LEGACY_SETTLES = gql(`
  mutation AdminApproveLegacySettles($settleIds: [Int!]!) {
    adminApproveLegacySettles(settleIds: $settleIds) {
      ok
      error
    }
  }
`);

const ADMIN_LEGACY_SETTLEMENT_CALENDAR = gql(`
  query AdminLegacySettlementCalendar($sellerId: Int!, $yearMonth: String!) {
    adminLegacySettlementCalendar(sellerId: $sellerId, yearMonth: $yearMonth) {
      date
      legacyEligibleAmount
      postCutoffReferenceAmount
      legacyEligibleSales {
        id
        name
        price
        sellerAmount
        sellerAmountWithFee
        fee
        soldAt
      }
      postCutoffReferenceSales {
        id
        name
        price
        sellerAmount
        sellerAmountWithFee
        fee
        soldAt
      }
    }
  }
`);

const SETTLE_WAY_OPTIONS = ["단말기", "팝빌", "계산서 발행 불가능"] as const;
const LEGACY_DEFAULT_MONTH = "2026-03";
const LEGACY_DEFAULT_DATE = new Date("2026-03-31T00:00:00");

function formatKRW(value: number): string {
  return `${value.toLocaleString("ko-KR")}원`;
}

function formatInvoiceAvailability(canIssueInvoice: boolean | null | undefined): string {
  if (canIssueInvoice == null) {
    return "-";
  }

  return canIssueInvoice ? "가능" : "불가능";
}

function escapeCsvCell(value: string | number | boolean | null | undefined): string {
  const cell = String(value ?? "");
  if (cell.includes(",") || cell.includes('"') || cell.includes("\n")) {
    return `"${cell.replace(/"/g, '""')}"`;
  }

  return cell;
}

export default function AdminLegacySettleManagement() {
  const [keyword, setKeyword] = useState("");
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null);
  const [selectedSettleWay, setSelectedSettleWay] = useState<
    (typeof SETTLE_WAY_OPTIONS)[number] | ""
  >("");
  const [selectedPendingIds, setSelectedPendingIds] = useState<Set<number>>(new Set());
  const [calendarYearMonth, setCalendarYearMonth] = useState(LEGACY_DEFAULT_MONTH);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(LEGACY_DEFAULT_DATE);

  const {
    data: unsettledStoresData,
    loading: unsettledStoresLoading,
    error: unsettledStoresError,
    refetch: refetchUnsettledStores,
  } = useQuery<AdminLegacyUnsettledStoresQuery>(ADMIN_LEGACY_UNSETTLED_STORES, {
    onError: (error) => {
      notifyError(error.message ?? "레거시 미정산 매장 조회 중 오류가 발생했습니다.");
    },
  });

  const {
    data: previewData,
    loading: previewLoading,
    refetch: refetchPreview,
  } = useQuery<
    AdminLegacyPendingSettlementPreviewQuery,
    AdminLegacyPendingSettlementPreviewQueryVariables
  >(ADMIN_LEGACY_PENDING_SETTLEMENT_PREVIEW, {
    variables: { sellerId: selectedSellerId ?? 0 },
    skip: selectedSellerId == null,
    onError: (error) => {
      notifyError(error.message ?? "레거시 정산 미리보기 조회 중 오류가 발생했습니다.");
    },
  });

  const {
    data: requestedSettlesData,
    loading: requestedSettlesLoading,
    refetch: refetchRequestedSettles,
  } = useQuery<AdminLegacyRequestedSettlesQuery>(ADMIN_LEGACY_REQUESTED_SETTLES, {
    onError: (error) => {
      notifyError(error.message ?? "레거시 승인 대기 목록 조회 중 오류가 발생했습니다.");
    },
  });

  const {
    data: calendarData,
    loading: calendarLoading,
    refetch: refetchCalendar,
  } = useQuery<
    AdminLegacySettlementCalendarQuery,
    AdminLegacySettlementCalendarQueryVariables
  >(ADMIN_LEGACY_SETTLEMENT_CALENDAR, {
    variables: {
      sellerId: selectedSellerId ?? 0,
      yearMonth: calendarYearMonth,
    },
    skip: selectedSellerId == null,
    onError: (error) => {
      notifyError(error.message ?? "레거시 캘린더 조회 중 오류가 발생했습니다.");
    },
  });

  const [requestLegacySettle, { loading: requestingLegacySettle }] = useMutation<
    AdminRequestLegacySettleMutation,
    AdminRequestLegacySettleMutationVariables
  >(ADMIN_REQUEST_LEGACY_SETTLE, {
    onCompleted: async (result) => {
      if (result.adminRequestLegacySettle.ok) {
        notify("레거시 정산요청이 생성되었습니다.");
        setSelectedPendingIds(new Set());
        setSelectedSettleWay("");
        await Promise.all([
          refetchUnsettledStores(),
          refetchRequestedSettles(),
          selectedSellerId != null
            ? refetchCalendar({ sellerId: selectedSellerId, yearMonth: calendarYearMonth })
            : Promise.resolve(),
        ]);
        return;
      }

      notifyError(
        result.adminRequestLegacySettle.error ??
          "레거시 정산요청 생성 중 오류가 발생했습니다."
      );
    },
    onError: (error) => {
      notifyError(error.message ?? "레거시 정산요청 생성 중 오류가 발생했습니다.");
    },
  });

  const [approveLegacySettles, { loading: approvingLegacySettles }] = useMutation<
    AdminApproveLegacySettlesMutation,
    AdminApproveLegacySettlesMutationVariables
  >(ADMIN_APPROVE_LEGACY_SETTLES, {
    onCompleted: async (result) => {
      if (result.adminApproveLegacySettles.ok) {
        notify("레거시 정산 승인 처리가 완료되었습니다.");
        setSelectedPendingIds(new Set());
        await Promise.all([refetchUnsettledStores(), refetchRequestedSettles()]);
        if (selectedSellerId != null) {
          await refetchPreview({ sellerId: selectedSellerId });
          await refetchCalendar({ sellerId: selectedSellerId, yearMonth: calendarYearMonth });
        }
        return;
      }

      notifyError(
        result.adminApproveLegacySettles.error ??
          "레거시 정산 승인 중 오류가 발생했습니다."
      );
    },
    onError: (error) => {
      notifyError(error.message ?? "레거시 정산 승인 중 오류가 발생했습니다.");
    },
  });

  const unsettledStores = useMemo(
    () => unsettledStoresData?.adminLegacyUnsettledStores ?? [],
    [unsettledStoresData]
  );
  const requestedSettles = useMemo(
    () => requestedSettlesData?.adminLegacyRequestedSettles ?? [],
    [requestedSettlesData]
  );

  const filteredStores = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();

    if (!normalizedKeyword) {
      return unsettledStores;
    }

    return unsettledStores.filter((store) => {
      const fields = [
        store.storeTitle,
        store.storeAddress ?? "",
        store.sellerName ?? "",
        store.contactNumber,
      ];

      return fields.some((field) => field.toLowerCase().includes(normalizedKeyword));
    });
  }, [keyword, unsettledStores]);

  const selectedStore = useMemo(
    () =>
      filteredStores.find((store) => store.sellerId === selectedSellerId) ??
      unsettledStores.find((store) => store.sellerId === selectedSellerId) ??
      null,
    [filteredStores, selectedSellerId, unsettledStores]
  );

  const selectedPreview = previewData?.adminLegacyPendingSettlementPreview ?? null;
  const calendarDays = calendarData?.adminLegacySettlementCalendar ?? [];

  useScrollToDetailPanelOnSelect(selectedStore);

  useEffect(() => {
    if (selectedSellerId == null) {
      return;
    }

    const exists = unsettledStores.some((store) => store.sellerId === selectedSellerId);
    if (!exists) {
      setSelectedSellerId(null);
      setSelectedSettleWay("");
    }
  }, [selectedSellerId, unsettledStores]);

  const handleRequestLegacySettle = async () => {
    if (!selectedStore) {
      notifyError("매장을 선택해주세요.");
      return;
    }

    if (!selectedSettleWay) {
      notifyError("정산 방식을 선택해주세요.");
      return;
    }

    if (!selectedPreview || selectedPreview.orderCount === 0) {
      notifyError("레거시 정산 대상 주문이 없습니다.");
      return;
    }

    if (!window.confirm(`${selectedStore.storeTitle}의 레거시 정산요청을 생성할까요?`)) {
      return;
    }

    await requestLegacySettle({
      variables: {
        sellerId: selectedStore.sellerId,
        settleWay: selectedSettleWay,
      },
    });
  };

  const handleTogglePendingSelection = (settleId: number) => {
    setSelectedPendingIds((previous) => {
      const next = new Set(previous);
      if (next.has(settleId)) {
        next.delete(settleId);
      } else {
        next.add(settleId);
      }
      return next;
    });
  };

  const handleApproveSelectedSettles = async () => {
    if (selectedPendingIds.size === 0) {
      notifyError("승인할 정산요청을 선택해주세요.");
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedPendingIds.size}건의 레거시 정산요청을 승인할까요?`
      )
    ) {
      return;
    }

    await approveLegacySettles({
      variables: {
        settleIds: Array.from(selectedPendingIds),
      },
    });
  };

  const handleDownloadStoresCsv = () => {
    if (filteredStores.length === 0) {
      notifyError("다운로드할 매장이 없습니다.");
      return;
    }

    const headers = [
      "매장ID",
      "매장명",
      "사장님명",
      "연락처",
      "사업자번호",
      "은행",
      "예금주",
      "계좌번호",
      "과세유형",
      "세금계산서발행",
      "대상총액",
      "정산예정금액",
      "정산정보",
      "기준일",
    ];

    const rows = filteredStores.map((store) => [
      store.storeId,
      store.storeTitle,
      store.sellerName ?? "",
      store.contactNumber,
      store.businessNumber ?? "",
      store.bank ?? "",
      store.accountHolder ?? "",
      store.accountNumber ?? "",
      store.taxationType ?? "",
      formatInvoiceAvailability(store.canIssueInvoice),
      store.totalPrice,
      store.settlePrice,
      store.isSettleInfoComplete ? "완료" : "미작성",
      store.cutoffDate,
    ]);

    const csvContent = [
      headers.map(escapeCsvCell).join(","),
      ...rows.map((row) => row.map(escapeCsvCell).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const today = new Date().toISOString().split("T")[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `레거시_정산_대상_매장_${today}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const allPendingSelected =
    requestedSettles.length > 0 &&
    requestedSettles.every((settle) => selectedPendingIds.has(settle.settleId));
  const isPendingIndeterminate =
    selectedPendingIds.size > 0 && selectedPendingIds.size < requestedSettles.length;

  if (unsettledStoresLoading && !unsettledStoresData) {
    return (
      <div className="w-full p-8 flex justify-center">
        <LoadingSpinnerInline />
      </div>
    );
  }

  if (unsettledStoresError && !unsettledStoresData) {
    return (
      <div className="w-full p-8 text-red-600">
        레거시 미정산 매장 목록을 불러오지 못했습니다.
      </div>
    );
  }

  return (
    <div className="w-full md:p-8 space-y-8">
      <AdminListDetailLayout
        title="레거시 정산 전용"
        description="2026-03-31까지의 미정산 잔여분 중 미요청 또는 거절 건만 별도로 요청하고 승인합니다."
        leftRatio={7}
        rightRatio={3}
        headerExtra={
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            이번 화면은 `미요청 + 거절` 주문만 포함합니다. `요청중` 또는 `승인` 상태로
            이미 묶인 주문은 제외됩니다.
          </div>
        }
        leftPanel={{
          title: `미요청/거절 매장 목록 (${filteredStores.length}곳)`,
          headerAction: (
            <button
              type="button"
              onClick={handleDownloadStoresCsv}
              disabled={filteredStores.length === 0}
              className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            >
              CSV 다운로드
            </button>
          ),
          searchPlaceholder: "매장명, 주소, 사장님명, 연락처로 검색",
          searchValue: keyword,
          onSearchChange: setKeyword,
          children: (
            <AdminStoreTableWithMobileCards
              stores={filteredStores.map((store) => ({
                id: store.storeId,
                title: store.storeTitle,
                address: store.storeAddress,
              }))}
              selectionMode="single"
              selectedIds={selectedStore ? [selectedStore.storeId] : []}
              onSelect={(store) => {
                const matchedStore =
                  filteredStores.find((row) => row.storeId === store.id) ?? null;
                setSelectedSellerId(matchedStore?.sellerId ?? null);
                setSelectedSettleWay("");
              }}
              showImage={false}
              showAddress={false}
              extraHeaders={
                <>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">사장님</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">은행</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">예금주</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">계좌번호</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">사업자번호</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">과세 유형</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">세금계산서 발행</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">정산 예정</th>
                  <th className="px-4 py-2 font-medium text-gray-700 break-keep">정산 정보</th>
                </>
              }
              renderExtraCells={(store) => {
                const matchedStore = filteredStores.find((row) => row.storeId === store.id);
                if (!matchedStore) {
                  return null;
                }

                return (
                  <>
                    <td className="px-4 py-3 text-sm text-gray-700 break-keep">
                      {matchedStore.sellerName ?? matchedStore.contactNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matchedStore.bank ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matchedStore.accountHolder ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-all">
                      {matchedStore.accountNumber ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matchedStore.businessNumber ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matchedStore.taxationType ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatInvoiceAvailability(matchedStore.canIssueInvoice)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {formatKRW(matchedStore.settlePrice)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          matchedStore.isSettleInfoComplete
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {matchedStore.isSettleInfoComplete ? "완료" : "미작성"}
                      </span>
                    </td>
                  </>
                );
              }}
              renderExtraCard={(store) => {
                const matchedStore = filteredStores.find((row) => row.storeId === store.id);
                if (!matchedStore) {
                  return null;
                }

                return (
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <div>사장님: {matchedStore.sellerName ?? matchedStore.contactNumber}</div>
                    <div>은행: {matchedStore.bank ?? "-"}</div>
                    <div>예금주: {matchedStore.accountHolder ?? "-"}</div>
                    <div>계좌번호: {matchedStore.accountNumber ?? "-"}</div>
                    <div>사업자번호: {matchedStore.businessNumber ?? "-"}</div>
                    <div>과세 유형: {matchedStore.taxationType ?? "-"}</div>
                    <div>세금계산서 발행: {formatInvoiceAvailability(matchedStore.canIssueInvoice)}</div>
                    <div className="font-semibold text-gray-900">
                      정산 예정: {formatKRW(matchedStore.settlePrice)}
                    </div>
                  </div>
                );
              }}
              emptyText={keyword.trim() ? "검색 결과가 없습니다." : "대상 매장이 없습니다."}
            />
          ),
        }}
        rightPanel={{
          title: "레거시 정산 요청",
          emptyText: "왼쪽 목록에서 매장을 선택하세요.",
          children: selectedStore ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
                <div className="font-semibold text-gray-900">{selectedStore.storeTitle}</div>
                <div>사장님: {selectedStore.sellerName ?? selectedStore.contactNumber}</div>
                <div>연락처: {selectedStore.contactNumber}</div>
                <div>정산 예정 금액: {formatKRW(selectedStore.settlePrice)}</div>
                {selectedStore.businessNumber ? (
                  <div>사업자번호: {selectedStore.businessNumber}</div>
                ) : null}
                <div>기준일: {selectedStore.cutoffDate} 포함</div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
                <div className="mb-2 font-semibold text-gray-900">계좌 정보</div>
                <div>은행: {selectedStore.bank ?? "-"}</div>
                <div>예금주: {selectedStore.accountHolder ?? "-"}</div>
                <div>계좌번호: {selectedStore.accountNumber ?? "-"}</div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
                <div className="mb-2 font-semibold text-gray-900">사업자 정보</div>
                <div>사업자번호: {selectedStore.businessNumber ?? "-"}</div>
                <div>과세 유형: {selectedStore.taxationType ?? "-"}</div>
                <div>
                  세금계산서 발행: {formatInvoiceAvailability(selectedStore.canIssueInvoice)}
                </div>
              </div>

              <LegacySettlementCalendar
                yearMonth={calendarYearMonth}
                days={calendarDays}
                loading={calendarLoading}
                selectedDate={selectedCalendarDate}
                onSelectDate={setSelectedCalendarDate}
                onChangeYearMonth={(yearMonth, nextSelectedDate) => {
                  setCalendarYearMonth(yearMonth);
                  setSelectedCalendarDate(nextSelectedDate);
                }}
              />

              {previewLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinnerInline />
                </div>
              ) : selectedPreview ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
                  <div className="font-semibold mb-2">이번 요청 포함 기준</div>
                  <div>미요청 + 거절 주문만 포함</div>
                  <div>요청중 / 승인 주문은 제외</div>
                  <div className="mt-3">대상 주문 수: {selectedPreview.orderCount}건</div>
                  <div>
                    미요청 {selectedPreview.unrequestedOrderCount}건 / 거절{" "}
                    {selectedPreview.rejectedOrderCount}건
                  </div>
                  <div>대상 총액: {formatKRW(selectedPreview.totalPrice)}</div>
                  <div className="font-semibold">정산 예정 금액: {formatKRW(selectedPreview.settlePrice)}</div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">정산 미리보기를 불러올 수 없습니다.</p>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  정산 방식
                </label>
                <select
                  value={selectedSettleWay}
                  onChange={(event) =>
                    setSelectedSettleWay(
                      event.target.value as (typeof SETTLE_WAY_OPTIONS)[number] | ""
                    )
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">선택하세요</option>
                  {SETTLE_WAY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {!selectedPreview?.isSettleInfoComplete ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  정산 정보가 완전하지 않습니다. 계좌/사업자/과세 정보를 확인해주세요.
                </div>
              ) : null}

              <button
                type="button"
                onClick={handleRequestLegacySettle}
                disabled={
                  requestingLegacySettle ||
                  previewLoading ||
                  !selectedSettleWay ||
                  selectedPreview?.orderCount === 0
                }
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {requestingLegacySettle ? "생성 중..." : "레거시 정산요청 생성"}
              </button>
            </div>
          ) : null,
        }}
      />

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                전용 승인 대기 목록 ({requestedSettles.length}건)
              </h2>
              <p className="text-sm text-gray-600">
                레거시 범위 주문만 연결된 `요청` 상태 정산요청만 승인할 수 있습니다.
              </p>
            </div>
            {selectedPendingIds.size > 0 ? (
              <button
                type="button"
                onClick={handleApproveSelectedSettles}
                disabled={approvingLegacySettles}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {approvingLegacySettles
                  ? "승인 중..."
                  : `선택 ${selectedPendingIds.size}건 승인`}
              </button>
            ) : null}
          </div>
        </div>

        {requestedSettlesLoading && !requestedSettlesData ? (
          <div className="flex justify-center py-8">
            <LoadingSpinnerInline />
          </div>
        ) : requestedSettles.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            승인 대기 중인 레거시 정산요청이 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allPendingSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = isPendingIndeterminate;
                        }
                      }}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedPendingIds(
                            new Set(requestedSettles.map((settle) => settle.settleId))
                          );
                          return;
                        }

                        setSelectedPendingIds(new Set());
                      }}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-700">매장명</th>
                  <th className="px-4 py-3 font-medium text-gray-700">요청일시</th>
                  <th className="px-4 py-3 font-medium text-gray-700">정산방식</th>
                  <th className="px-4 py-3 font-medium text-gray-700">주문 수</th>
                  <th className="px-4 py-3 font-medium text-gray-700">정산금액</th>
                  <th className="px-4 py-3 font-medium text-gray-700">상태</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {requestedSettles.map((settle) => {
                  const isSelected = selectedPendingIds.has(settle.settleId);

                  return (
                    <tr
                      key={settle.settleId}
                      className={isSelected ? "bg-green-50" : "hover:bg-gray-50"}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleTogglePendingSelection(settle.settleId)}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="px-4 py-3">{settle.storeTitle ?? "-"}</td>
                      <td className="px-4 py-3">
                        {new Date(settle.requestDate).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-3">{settle.settleWay}</td>
                      <td className="px-4 py-3">{settle.orderCount}건</td>
                      <td className="px-4 py-3 font-medium">
                        {formatKRW(settle.settlePrice)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                          {settle.state}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
